/**
 * Capability Gates
 *
 * Runtime invariants for the capability-aware setup flow.
 *
 * `canApply` invariants on a CDSE store can only inspect that store's
 * state. Capability gating cuts across stores: the channels submit
 * policy needs to know whether the selected connector declares `oauth`
 * or `api-key` before it picks a branch, but capabilities live in the
 * shared systems-integrations store.
 *
 * These gates centralize that check so the policy doesn't open-code an
 * id-based switch and so we can assert "submitOAuth requires the
 * connector declares the oauth capability" in one place.
 */

import { Capability } from '@bett3r-dev/teselly-domain';
import { ConnectorEntry } from './state';

/**
 * Panel kinds, keyed by the dominant capability of a connector. Drives
 * which setup-panel component is rendered.
 *
 * Precedence: `oauth` > `api-key` > `direct-provision`. A connector that
 * supports OAuth defaults to the OAuth flow; an api-key connector to the
 * API-key form; a `direct-provision` connector (e.g. retailstore) to the
 * direct-provision config panel. Anything else falls back to the
 * server-configured read-only panel.
 */
export type SetupPanelKind =
  | 'oauth'
  | 'apiKey'
  | 'directProvision'
  | 'serverConfigured';

const hasCapability = (
  connector: ConnectorEntry | undefined,
  capability: Capability
): boolean => Boolean( connector?.capabilities?.includes( capability ));

/**
 * Resolve the setup-panel kind for a connector based on its declared
 * capabilities. This is the single source of truth that both the modal
 * (for rendering) and the submit policy (for branching) read from.
 */
export const resolveSetupPanelKind = (
  connector: ConnectorEntry | undefined
): SetupPanelKind => {
  if ( hasCapability( connector, 'oauth' )) return 'oauth';
  if ( hasCapability( connector, 'api-key' )) return 'apiKey';
  if ( hasCapability( connector, 'direct-provision' )) return 'directProvision';
  return 'serverConfigured';
};

/**
 * Gate: submitting through the OAuth branch requires the connector to
 * declare the `oauth` capability. Returns `true` when the action is
 * permitted, `false` otherwise. The caller maps `false` to a UI error
 * or a policy short-circuit.
 */
export const canSubmitOAuth = (
  connector: ConnectorEntry | undefined
): boolean => hasCapability( connector, 'oauth' );

/**
 * Gate: submitting through the API-key branch requires the connector
 * to declare the `api-key` capability.
 */
export const canSubmitApiKey = (
  connector: ConnectorEntry | undefined
): boolean => hasCapability( connector, 'api-key' );

/**
 * Gate: provisioning through the direct-provision branch requires the
 * connector to declare the `direct-provision` capability. These
 * connectors (e.g. retailstore) have no OAuth flow and no API-key form,
 * but the user does supply config (name, warehouses, price lists).
 */
export const canDirectProvision = (
  connector: ConnectorEntry | undefined
): boolean => hasCapability( connector, 'direct-provision' );

/**
 * Gate: server-configured connectors expose no user-facing setup actions —
 * no OAuth flow, no API-key form, and no direct-provision config. Any
 * setup-submit dispatch against such a connector is a contract violation.
 * A `direct-provision` connector DOES have a setup action, so it is
 * explicitly excluded here (mirrors `resolveSetupPanelKind`'s precedence).
 */
export const isServerConfigured = (
  connector: ConnectorEntry | undefined
): boolean =>
  !hasCapability( connector, 'oauth' ) &&
  !hasCapability( connector, 'api-key' ) &&
  !hasCapability( connector, 'direct-provision' );

/**
 * Connector ids whose channel client can seed the account with the channel's
 * FULL past order history (TV1-2379).
 *
 * ── Why an id list and not a declared `Capability` ────────────────────────
 *
 * The backend gate is `ChannelAPIClient.getTotalOrdersToImport`, an OPTIONAL
 * method on the client interface — `StartChannelOrderImportBatchProcess` throws
 * `BATCH_PROCESSES_CHANNEL_CAPABILITY_UNSUPPORTED` when the connector does not
 * implement it. It is NOT a value in `CapabilityList`, so there is nothing on
 * the connectors readmodel that reports it.
 *
 * The nearest declared capability, `syncs-orders`, is the WRONG gate: amazon,
 * mercadolibre, shopify, tiendanube and woocommerce all declare it, and only
 * mercadolibre implements the count. Gating on it would offer the control on
 * four connectors that then 400.
 *
 * So: one list, in one place. When a second connector implements
 * `getTotalOrdersToImport`, adding its id here is the whole front-end change.
 * If the set ever grows past a handful, promote it to a real `Capability` on
 * the connector readmodel and delete this.
 */
export const CONNECTORS_WITH_ORDER_HISTORY_IMPORT = new Set([ 'mercadolibre', 'tiendanube' ]);

/**
 * Gate: offering / firing the historical order import requires the connector to
 * be able to count what it is about to pull. Keyed by connector id rather than
 * by `ConnectorEntry` because both the policy (which only ever holds the parked
 * connector id) and the two UI surfaces need the same answer.
 */
export const canImportOrderHistory = ( connectorId: string | undefined ): boolean =>
  Boolean( connectorId && CONNECTORS_WITH_ORDER_HISTORY_IMPORT.has( connectorId ));
